import { useUser } from '@clerk/clerk-react';
import { Building2, Shield, Users } from 'lucide-react';
import React from 'react';
import { Skeleton } from '../ui/skeleton';

/**
 * UserProfile Component
 *
 * Displays the current user's profile information in the sidebar including:
 * - User avatar (40x40px) with fallback to initials
 * - User full name (firstName + lastName)
 * - Role badge with color-coded icon based on user role
 *
 * Uses Clerk for user data and lucide-react for role icons.
 * Supports three roles: branch_manager, restaurant_admin, super_admin
 *
 * @component
 * @example
 * // In Sidebar.tsx at the top
 * <UserProfile />
 */
export const UserProfile: React.FC = () => {
  const { user, isLoaded } = useUser();

  // Loading state: show skeleton while Clerk loads user data
  if (!isLoaded) {
    return (
      <div className="px-4 py-3" style={{ borderBottom: `1px solid var(--border)` }}>
        <div className="flex items-center gap-3">
          {/* Avatar skeleton */}
          <Skeleton className="h-10 w-10 rounded-full" />

          {/* Name and role skeleton */}
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-3 w-16" />
          </div>
        </div>
      </div>
    );
  }

  // If no user, don't render (shouldn't happen with auth guard, but handle gracefully)
  if (!user) {
    return null;
  }

  // Extract user data from Clerk
  const firstName = user.firstName || '';
  const lastName = user.lastName || '';
  const fullName = `${firstName} ${lastName}`.trim() || 'User';
  const avatarUrl = user.imageUrl;
  
  // Extract role from Clerk metadata (roles is an array, similar to AdminLayout)
  const userRoles = (user?.publicMetadata?.roles as string[] | undefined) || [];
  const isSuperAdmin = Array.isArray(userRoles) && userRoles.includes('super_admin');
  const isBranchManager = Array.isArray(userRoles) && userRoles.includes('branch_manager');
  
  // Determine role (matching AdminLayout logic)
  const role = isSuperAdmin
    ? 'super_admin'
    : isBranchManager
    ? 'branch_manager'
    : 'restaurant_admin';

  // Generate user initials for fallback avatar
  const getInitials = (): string => {
    if (firstName && lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase();
    }
    if (firstName) {
      return firstName[0].toUpperCase();
    }
    return 'U';
  };

  // Format role from snake_case to human-readable format
  const formatRole = (roleValue: string): string => {
    if (!roleValue) return 'User';
    return roleValue
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Determine role color and icon based on user role
  const getRoleStyle = (
    roleValue: string,
  ): {
    color: string;
    bgColor: string;
    icon: React.ReactNode;
  } => {
    switch (roleValue) {
      case 'branch_manager':
        return {
          color: 'var(--role-manager-text)',
          bgColor: 'var(--role-manager-bg)',
          icon: <Users size={14} />,
        };
      case 'restaurant_admin':
        return {
          color: 'var(--role-admin-text)',
          bgColor: 'var(--role-admin-bg)',
          icon: <Building2 size={14} />,
        };
      case 'super_admin':
        return {
          color: 'var(--role-admin-text)',
          bgColor: 'var(--role-admin-bg)',
          icon: <Shield size={14} />,
        };
      default:
        return {
          color: 'var(--role-user-text)',
          bgColor: 'var(--role-user-bg)',
          icon: null,
        };
    }
  };

  const roleStyle = getRoleStyle(role);
  const formattedRole = formatRole(role);

  return (
    <div className="px-4 py-3" style={{ borderBottom: `1px solid var(--border)` }}>
      <div className="flex items-center gap-3">
        {/* Avatar */}
        <div className="flex-shrink-0">
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt={fullName}
              className="h-10 w-10 rounded-full object-cover"
              loading="lazy"
            />
          ) : (
            <div className="h-10 w-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--primary-medium)' }}>
              <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                {getInitials()}
              </span>
            </div>
          )}
        </div>

        {/* Name and Role */}
        <div className="flex-1 min-w-0">
          {/* User name */}
          <p className="text-sm font-semibold text-[var(--text-primary)] truncate">
            {fullName}
          </p>

          {/* Role badge */}
          {Array.isArray(userRoles) && userRoles.length > 0 && role && (
            <div
              className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium mt-1"
              style={{
                color: roleStyle.color,
                backgroundColor: roleStyle.bgColor,
              }}
              title={formattedRole}
            >
              {roleStyle.icon}
              <span className="truncate">{formattedRole}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

UserProfile.displayName = 'UserProfile';
